<script setup>
import {computed, inject, reactive} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog, Select} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {usePaymentMethodsStore} from "@/stores/payment.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfigData} from "@/composables/useConfigData.js";
import {useVuelidate} from "@vuelidate/core";

//stores
const userStore = useUserStore();
const paymentMethodsStore = usePaymentMethodsStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  methods: {
    type: Array,
    required: true
  },
  selectedMethod: {
    type: Object,
    required: false
  }
});

//injects
const visible = inject('isShowAddPaymentMethodModal')

//component variables

const types = computed(() => {
  if (props.selectedMethod) {
    return [props.selectedMethod.type]
  }
  if (props.methods.length > 0) {
    if (props.methods?.find(m => m.type === 'Rial')) {
      paymentMethod.type = 'Crypto'
      return ['Crypto']
    } else if (props.methods?.find(m => m.type === 'Crypto')) {
      paymentMethod.type = 'Rial'
      return ['Rial']
    }
  } else {
    return ['Crypto', 'Rial']
  }
})
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const paymentMethod = reactive({
  id: props.selectedMethod?.id ? props.selectedMethod?.id : null,
  type: props.selectedMethod?.type === 'Crypto' || !props.selectedMethod?.type ? 'Crypto' : 'Rial',
  name: props.selectedMethod?.name || '',
  rial: {
    iban: props.selectedMethod?.rial?.iban || '',
    cardNumber: props.selectedMethod?.rial?.cardNumber || '',
  },
  crypto: {
    network: props.selectedMethod?.crypto?.network || 'USDT BEP20 (BSC)',
    address: props.selectedMethod?.crypto?.address || '',
  },
})

//global functions
//component functions
const onSave = async () => {
  setSaving(true)
  const payload = {
    profile: { id: props.user.id },
    ...paymentMethod
  }
  const isEdit = !!props.selectedMethod?.id
  const action = isEdit
      ? paymentMethodsStore.action_updatePaymentMethod2
      : paymentMethodsStore.action_addPaymentMethod2
  const successMessage = isEdit
      ? "Payment Method Edited!"
      : "New Payment Method Added!"
  const res = await action(payload)
  if (res.result) {
    showMessage(successMessage, "success", 1500)
    emit('refresh')
    visible.value = false
  } else {
    showMessage(res, 'error')
  }
  setSaving(false)
}
const selectType = (type) => {
  paymentMethod.type = type
}

//hooks

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        {{ props.selectedMethod ? 'Edit Payment Method' : 'Add New Payment Method' }}
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-96 max-w-full">
      <div class="grid grid-cols-12 gap-4">
        <div
            class="col-span-12 flex flex-row items-center gap-1 text-xs 2xl:text-sm p-1 rounded-md border border-slate-700">
          <div v-for="type in types" class="flex w-full items-center justify-center p-1 rounded-md cursor-pointer"
               @click="selectType(type)"
               :class="[paymentMethod.type === type ? 'bg-slate-700' : '']">
            {{ type }}
          </div>
        </div>
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText v-model="paymentMethod.name" id="name" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="name" class="!bg-slate-900">Name</label>
          </FloatLabel>
        </div>
        <div v-if="paymentMethod.type === 'Crypto'" class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText v-model="paymentMethod.crypto.network" id="network" autocomplete="off" disabled readonly
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="network" class="!bg-slate-900">Network</label>
          </FloatLabel>
        </div>
        <div v-if="paymentMethod.type === 'Crypto'" class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText v-model="paymentMethod.crypto.address" id="address" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="address" class="!bg-slate-900">Address</label>
          </FloatLabel>
        </div>
        <div v-if="paymentMethod.type === 'Rial'" class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText v-model="paymentMethod.rial.iban" id="iban" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="iban" class="!bg-slate-900">Iban</label>
          </FloatLabel>
        </div>
        <div v-if="paymentMethod.type === 'Rial'" class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText v-allow-digits v-model="paymentMethod.rial.cardNumber" id="cardNumber" autocomplete="off" maxlength="16"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="cardNumber" class="!bg-slate-900">Card Number</label>
          </FloatLabel>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-3 select-none">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Save
      </button>
    </div>
  </Dialog>
</template>
<style></style>